import { FC, useState } from 'react'
import { ReservationService } from '../../../../../Types/HotelBookingTypes'
import { i18n } from '../../../../translate/i18n'

interface ServicesTabProps {
  services: ReservationService[]
}

const ServicesTab: FC<ServicesTabProps> = ({ services }) => {
  const [expandedServices, setExpandedServices] = useState<Set<number>>(new Set())

  const toggleService = (serviceId: number) => {
    setExpandedServices(prev => {
      const newSet = new Set(prev)
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId)
      } else {
        newSet.add(serviceId)
      }
      return newSet
    })
  }
  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'badge-success'
      case 'pending':
        return 'badge-warning'
      case 'cancelled':
        return 'badge-danger'
      case 'completed':
        return 'badge-info'
      default:
        return 'badge-secondary'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return i18n.t('hospitality_room_reservation_tabs.services.status_confirmed')
      case 'pending':
        return i18n.t('hospitality_room_reservation_tabs.services.status_pending')
      case 'cancelled':
        return i18n.t('hospitality_room_reservation_tabs.services.status_cancelled')
      case 'completed':
        return i18n.t('hospitality_room_reservation_tabs.services.status_completed')
      default:
        return status
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTotalAmount = () => {
    return services.reduce((total, service) => total + (service.price || 0), 0)
  }

  const getTotalPeople = () => {
    return services.reduce((total, service) => total + (service.peopleQty || 0), 0)
  }

  if (services.length === 0) {
    return (
      <div className='card'>
        <div className='card-body text-center py-10'>
          <i className='fas fa-concierge-bell text-muted fs-3x mb-4'></i>
          <h3 className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.no_services')}</h3>
          <p className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.no_services_description')}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Resumo dos Serviços */}
      <div className='row mb-6'>
        <div className='col-md-4'>
          <div className='card card-custom bg-light-primary'>
            <div className='card-body text-center p-4'>
              <i className='fas fa-concierge-bell text-primary fs-2x mb-3'></i>
              <h3 className='text-primary fw-bold'>{services.length}</h3>
              <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.services.total_services')}</p>
            </div>
          </div>
        </div>
        <div className='col-md-4'>
          <div className='card card-custom bg-light-success'>
            <div className='card-body text-center p-4'>
              <i className='fas fa-money-bill-wave text-success fs-2x mb-3'></i>
              <h3 className='text-success fw-bold'>R$ {getTotalAmount().toFixed(2)}</h3>
              <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.services.total_amount')}</p>
            </div>
          </div>
        </div>
        <div className='col-md-4'>
          <div className='card card-custom bg-light-info'>
            <div className='card-body text-center p-4'>
              <i className='fas fa-users text-info fs-2x mb-3'></i>
              <h3 className='text-info fw-bold'>{getTotalPeople()}</h3>
              <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.services.total_people')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Serviços */}
      <div className='row g-5'>
        {services.map((service) => {
          const isExpanded = expandedServices.has(service.id)
          return (
          <div key={service.id} className='col-12'>
            <div className='card card-custom shadow-sm'>
              <div 
                className='card-header bg-light' 
                style={{ cursor: 'pointer' }}
                onClick={() => toggleService(service.id)}
              >
                <div className='card-title'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-50px me-3'>
                      <img
                        src={service.post?.img || '/media/svg/files/blank-image.svg'}
                        alt={service.post?.title}
                        className='rounded'
                        onError={(e) => {
                          e.currentTarget.src = '/media/svg/files/blank-image.svg'
                        }}
                      />
                    </div>
                    <div>
                      <h3 className='fw-bold mb-1'>{service.post?.title || service.content?.metadata?.serviceTitle || 'Serviço'}</h3>
                      <span className='text-muted fs-7'>
                        {i18n.t('hospitality_room_reservation_tabs.services.service_id')}: #{service.id}
                      </span>
                    </div>
                  </div>
                </div>
                <div className='card-toolbar d-flex align-items-center'>
                  <span className={`badge ${getStatusBadgeClass(service.status)} fs-7 me-3`}>
                    {getStatusLabel(service.status)}
                  </span>
                  <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
                </div>
              </div>

              {isExpanded && (
              <>
              <div className='card-body'>
                <div className='row'>
                  {/* Informações do Serviço */}
                  <div className='col-md-6'>
                    <div className='mb-4'>
                      <h5 className='text-gray-700 fw-bold mb-3'>
                        <i className='fas fa-info-circle text-primary me-2'></i>
                      </h5>
                      
                      {service.content?.service && (
                        <div className='mb-3'>
                          <div className='d-flex justify-content-between mb-2'>
                            <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.service_name')}:</span>
                            <span className='fw-bold'>{service.content.service.name}</span>
                          </div>
                          <div className='d-flex justify-content-between mb-2'>
                            <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.booking_id')}:</span>
                            <span className='fw-bold'>#{service.content.service.bookingId}</span>
                          </div>
                        </div>
                      )}

                      <div className='d-flex justify-content-between mb-2'>
                        <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.reservation_date')}:</span>
                        <span className='fw-bold'>{formatDate(service.reservationDate)}</span>
                      </div>

                      {service.reservationTime && (
                        <div className='d-flex justify-content-between mb-2'>
                          <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.reservation_time')}:</span>
                          <span className='fw-bold'>{service.reservationTime}</span>
                        </div>
                      )}

                      <div className='d-flex justify-content-between mb-2'>
                        <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.people_qty')}:</span>
                        <span className='fw-bold'>{service.peopleQty}</span>
                      </div>

                      <div className='d-flex justify-content-between mb-2'>
                        <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.submitted_at')}:</span>
                        <span className='fw-bold'>{formatDateTime(service.content?.metadata?.submittedAt || service.created)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Informações do Quarto e Valores */}
                  <div className='col-md-6'>
                    <div className='mb-4'>
                      <h5 className='text-gray-700 fw-bold mb-3'>
                        <i className='fas fa-bed text-info me-2'></i>
                        {i18n.t('hospitality_room_reservation_tabs.services.room_info')}
                      </h5>
                      
                      {service.content?.room && (
                        <>
                          <div className='d-flex justify-content-between mb-2'>
                            <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.room_number')}:</span>
                            <span className='fw-bold'>{service.content.room.roomNumber}</span>
                          </div>
                          <div className='d-flex justify-content-between mb-2'>
                            <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.room_type')}:</span>
                            <span className='fw-bold text-capitalize'>{service.content.room.type}</span>
                          </div>
                          <div className='d-flex justify-content-between mb-2'>
                            <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.room_price')}:</span>
                            <span className='fw-bold'>R$ {service.content.room.price.toFixed(2)}</span>
                          </div>
                        </>
                      )}

                      <div className='separator my-3'></div>

                      <div className='d-flex justify-content-between mb-2'>
                        <span className='text-muted fs-5 fw-bold'>{i18n.t('hospitality_room_reservation_tabs.services.total_price')}:</span>
                        <span className='text-success fs-4 fw-bold'>R$ {service.price.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Descrição do Serviço */}
                {service.post?.content && (
                  <div className='mt-4'>
                    <h5 className='text-gray-700 fw-bold mb-3'>
                      <i className='fas fa-align-left text-warning me-2'></i>
                      {i18n.t('hospitality_room_reservation_tabs.services.description')}
                    </h5>
                    <div 
                      className='text-gray-600'
                      dangerouslySetInnerHTML={{ __html: service.post.content }}
                      style={{ maxHeight: '150px', overflow: 'auto' }}
                    />
                  </div>
                )}

                {/* Configurações do Grid de Reserva */}
                {service.gridHotelBooking && (
                  <div className='mt-4'>
                    <h5 className='text-gray-700 fw-bold mb-3'>
                      <i className='fas fa-cog text-secondary me-2'></i>
                      {i18n.t('hospitality_room_reservation_tabs.services.booking_details')}
                    </h5>
                    <div className='bg-light p-3 rounded'>
                      <div className='row'>
                        <div className='col-md-6'>
                          <div className='mb-2'>
                            <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.booking_title')}:</span>
                            <span className='fw-bold ms-2'>{service.gridHotelBooking.title}</span>
                          </div>
                          <div className='mb-2'>
                            <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.capacity')}:</span>
                            <span className='fw-bold ms-2'>{service.gridHotelBooking.capacity}</span>
                          </div>
                        </div>
                        <div className='col-md-6'>
                          <div className='mb-2'>
                            <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.multiple')}:</span>
                            <span className='fw-bold ms-2'>
                              {service.gridHotelBooking.multiple ? 
                                i18n.t('hospitality_room_reservation_tabs.services.yes') : 
                                i18n.t('hospitality_room_reservation_tabs.services.no')}
                            </span>
                          </div>
                          <div className='mb-2'>
                            <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.services.description')}:</span>
                            <span className='fw-bold ms-2'>{service.gridHotelBooking.description}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Descrição adicional */}
                {service.description && (
                  <div className='mt-4'>
                    <div className='alert alert-info d-flex align-items-start'>
                      <i className='fas fa-sticky-note me-3 mt-1'></i>
                      <div>
                        <strong>{i18n.t('hospitality_room_reservation_tabs.services.notes')}:</strong>
                        <p className='mb-0 mt-2'>{service.description}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className='card-footer bg-light'>
                <div className='d-flex justify-content-between align-items-center'>
                  <div className='text-muted fs-7'>
                    <i className='fas fa-calendar-plus me-1'></i>
                    {i18n.t('hospitality_room_reservation_tabs.services.created_at')}: {formatDateTime(service.created)}
                  </div>
                  <div className='text-muted fs-7'>
                    <i className='fas fa-calendar-check me-1'></i>
                    {i18n.t('hospitality_room_reservation_tabs.services.updated_at')}: {formatDateTime(service.updated)}
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        )})}      </div>
    </div>
  )
}

export default ServicesTab
